<template>
  <v-card flat>
    <b-row>
      <b-col lg="3" md="6">
        <b-form-group :label="$t('FORM_LABELS.MACHINE')">
          <FormSelect
            v-model="form.machineId"
            :options="machines"
            :validateState="validateState('machineId')"
            :disabled="readonly"
            clearable
            @input="onMachineChange"
          />
        </b-form-group>
      </b-col>
      <b-col lg="3" md="6">
        <b-form-group :label="$t('FORM_LABELS.PRIORITY')">
          <FormSelect
            v-model="form.priorityId"
            :options="priorities"
            :validateState="validateState('priorityId')"
            :disabled="readonly"
            clearable
          />
        </b-form-group>
      </b-col>
      <b-col v-if="showStatus" lg="3" md="6">
        <b-form-group :label="$t('FORM_LABELS.STATUS')">
          <FormSelect
            v-model="form.statusId"
            :options="statuses"
            :validateState="validateState('statusId')"
            :disabled="readonly"
            clearable
          />
          <small
            v-if="isFinishedStatusSelected && $v.form.statusId.$dirty && !$v.form.statusId.tasksTerminal"
            class="text-danger"
          >
            {{ $t("SERVICE_VISITS.TASKS_MUST_BE_TERMINAL") }}
          </small>
        </b-form-group>
      </b-col>
      <b-col v-if="isFinishedStatusSelected" lg="3" md="6">
        <b-form-group :label="completionMileageLabel">
          <FormInput
            v-model="form.serviceMileage"
            type="number"
            min="0"
            step="1"
            :i18n="{ placeholder: completionMileageLabel }"
            :validateState="validateState('serviceMileage')"
            :disabled="readonly"
          />
        </b-form-group>
      </b-col>
      <b-col v-if="isFinishedStatusSelected" lg="3" md="6">
        <b-form-group :label="$t('SERVICE_VISITS.HOURS_WORKED')">
          <FormInput
            v-model="form.hoursWorked"
            type="number"
            min="0"
            step="0.25"
            :i18n="{ placeholder: 'SERVICE_VISITS.HOURS_WORKED' }"
            :validateState="validateState('hoursWorked')"
            :disabled="readonly"
          />
        </b-form-group>
      </b-col>
      <b-col v-if="isFinishedStatusSelected" lg="6" md="12">
        <b-form-group :label="$t('SERVICE_VISITS.WORK_DONE')">
          <b-form-textarea
            v-model="form.workDone"
            rows="2"
            max-rows="5"
            maxlength="5000"
            :placeholder="$t('SERVICE_VISITS.WORK_DONE')"
            :disabled="readonly"
          />
        </b-form-group>
      </b-col>
      <b-col lg="3" md="6">
        <b-form-group :label="$t('FORM_LABELS.IS_ON_ROAD')">
          <FormSwitch
            v-model="form.isOnRoad"
            type="checkbox"
            name="isOnRoad"
            :disabled="readonly"
          />
        </b-form-group>
      </b-col>
      <b-col v-if="!form.isOnRoad" lg="3" md="6">
        <b-form-group :label="$t('FORM_LABELS.SERVICE_LOCATION')">
          <FormSelect
            v-model="form.serviceLocationId"
            :options="serviceLocations"
            reduceKey="id"
            :validateState="validateState('serviceLocationId')"
            :disabled="readonly"
            clearable
          />
        </b-form-group>
      </b-col>
      <b-col v-if="form.isOnRoad" lg="3" md="6">
        <b-form-group :label="$t('FORM_LABELS.ON_ROAD_NOTE')">
          <b-form-textarea
            v-model="form.onRoadNote"
            rows="1"
            max-rows="4"
            maxlength="255"
            :disabled="readonly"
          />
        </b-form-group>
      </b-col>
      <b-col lg="3" md="6">
        <b-form-group :label="$t('FORM_LABELS.DATE_FROM')">
          <FormDatepicker
            v-model="form.dateFrom"
            :validateState="validateState('dateFrom')"
            :disabled="readonly"
          />
        </b-form-group>
      </b-col>
      <b-col lg="3" md="6">
        <b-form-group :label="$t('FORM_LABELS.DATE_TO')">
          <FormDatepicker
            v-model="form.dateTo"
            :validateState="validateState('dateTo')"
            :disabled="readonly"
          />
        </b-form-group>
      </b-col>
      <b-col v-if="!isOutsideService" lg="3" md="6">
        <b-form-group :label="$t('FORM_LABELS.DURATION')">
          <FormInput
            v-model="form.duration"
            type="number"
            step="0.25"
            min="0"
            :i18n="{ placeholder: 'FORM_LABELS.DURATION' }"
            :validateState="validateState('duration')"
            :disabled="readonly"
          />
        </b-form-group>
      </b-col>
      <b-col v-if="!isOutsideService" lg="3" md="6">
        <b-form-group :label="$t('MENU.ITEM.SERVICE.MECHANICS')">
          <FormSelect
            v-model="form.mechanicIds"
            :options="mechanics"
            multiple
            :disabled="readonly"
            clearable
          />
        </b-form-group>
      </b-col>
    </b-row>

    <div class="separator separator-dashed my-6"></div>

    <b-row>
      <b-col md="12">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0">{{ $t("SERVICE_VISITS.CUSTOM_TASKS") }}</h5>
          <button
            v-if="!readonly"
            type="button"
            class="btn btn-light-success font-weight-bolder"
            @click="addCustomTask"
          >
            <i class="flaticon2-plus"></i>{{ $t("LABELS.ADD_NEW") }}
          </button>
        </div>
        <ServiceVisitCustomTaskRow
          v-for="(customTask, index) in form.customTasks"
          :key="customTask.localId || customTask.id || index"
          :task="customTask"
          :item-statuses="itemStatuses"
          :readonly="readonly"
          :show-remove="!readonly"
          @remove="removeCustomTask(index)"
        />
        <div v-if="!form.customTasks.length">
          {{ $t("SERVICE_VISITS.NO_CUSTOM_TASKS") }}
        </div>

        <template v-if="form.machineId">
          <div class="separator separator-dashed my-6"></div>

          <h5 class="mb-3">{{ $t("FORM_LABELS.SERVICE_TYPES") }}</h5>
          <div v-if="!serviceTypes.length">
            {{ $t("SERVICE_VISITS.NO_SERVICE_TYPES") }}
          </div>
          <div
            v-for="type in serviceTypes"
            :key="type.id"
            class="mb-3"
          >
            <b-row>
              <b-col :lg="isServiceTypeSelected(type.id) ? 5 : 12">
                <b-form-checkbox
                  v-model="form.serviceTypeIds"
                  :value="type.id"
                  :disabled="readonly"
                >
                  <strong>{{ type.name }}</strong>
                  <span v-if="type.serviceStatus" class="d-block">
                    {{ type.serviceStatus.name }}
                  </span>
                  <span v-if="type.lastService" class="d-block">
                    {{ $t("SERVICE_VISITS.LAST_SERVICE") }}:
                    {{ formatLastService(type.lastService) }}
                  </span>
                  <span
                    v-if="type.lastService && type.lastService.description"
                    class="d-block"
                  >
                    {{ type.lastService.description }}
                  </span>
                  <span v-if="!type.lastService" class="d-block">
                    {{ $t("SERVICE_VISITS.NO_LAST_SERVICE") }}
                  </span>
                </b-form-checkbox>
              </b-col>
              <b-col
                v-if="isServiceTypeSelected(type.id)"
                lg="3"
                class="mt-2 mt-lg-0"
              >
                <FormSelect
                  :value="serviceTypeStatusId(type.id)"
                  :options="itemStatuses"
                  :placeholder="$t('FORM_LABELS.STATUS')"
                  :disabled="readonly"
                  @input="setServiceTypeStatusId(type.id, $event)"
                />
              </b-col>
              <b-col
                v-if="isServiceTypeSelected(type.id)"
                lg="4"
                class="mt-2 mt-lg-0"
              >
                <b-form-textarea
                  rows="2"
                  max-rows="4"
                  :value="serviceTypeNote(type.id)"
                  :placeholder="$t('FORM_LABELS.NOTE')"
                  :disabled="readonly"
                  @input="setServiceTypeNote(type.id, $event)"
                />
              </b-col>
            </b-row>
          </div>

          <div class="separator separator-dashed my-6"></div>

          <h5 class="mb-3">{{ $t("MACHINE_SERVICE_SIGNALS.TITLE") }}</h5>
          <div v-if="!machineServiceSignals.length">
            {{ $t("SERVICE_VISITS.NO_SIGNALS") }}
          </div>
          <div
            v-for="signal in machineServiceSignals"
            :key="signal.id"
            class="mb-2"
          >
            <b-row>
              <b-col :lg="isMachineServiceSignalSelected(signal.id) ? 5 : 12">
                <b-form-checkbox
                  v-model="form.machineServiceSignalIds"
                  :value="signal.id"
                  :disabled="readonly"
                >
                  <strong>{{ signal.dateTime }}</strong>
                  <span class="d-block">{{ signal.description }}</span>
                </b-form-checkbox>
              </b-col>
              <b-col
                v-if="isMachineServiceSignalSelected(signal.id)"
                lg="3"
                class="mt-2 mt-lg-0"
              >
                <FormSelect
                  :value="machineServiceSignalStatusId(signal.id)"
                  :options="itemStatuses"
                  :placeholder="$t('FORM_LABELS.STATUS')"
                  :disabled="readonly"
                  @input="setMachineServiceSignalStatusId(signal.id, $event)"
                />
              </b-col>
              <b-col
                v-if="isMachineServiceSignalSelected(signal.id)"
                lg="4"
                class="mt-2 mt-lg-0"
              >
                <b-form-textarea
                  rows="2"
                  max-rows="4"
                  :value="machineServiceSignalNote(signal.id)"
                  :placeholder="$t('FORM_LABELS.NOTE')"
                  :disabled="readonly"
                  @input="setMachineServiceSignalNote(signal.id, $event)"
                />
              </b-col>
            </b-row>
          </div>
        </template>
        <div v-else>
          {{ $t("SERVICE_VISITS.SELECT_MACHINE_FIRST") }}
        </div>
      </b-col>
    </b-row>
  </v-card>
</template>

<script>
import { validationMixin } from "vuelidate";
import { required, requiredIf } from "vuelidate/lib/validators";
import { formBuilderMixin } from "@/core/mixins/formBuilderMixin.js";
import {
  FETCH_MACHINE,
  FETCH_MACHINES
} from "@/modules/machines/store/machine.module";
import {
  FETCH_SERVICE_VISIT_ITEM_STATUSES,
  FETCH_SERVICE_VISIT_MACHINE_SERVICE_SIGNALS,
  FETCH_SERVICE_VISIT_MECHANICS,
  FETCH_SERVICE_VISIT_PRIORITIES,
  FETCH_SERVICE_VISIT_SERVICE_LOCATIONS,
  FETCH_SERVICE_VISIT_STATUSES,
  FETCH_SERVICE_VISIT_SERVICE_TYPES
} from "@/modules/service-visits/store/service-visits.module";
import FormInput from "@/view/content/forms/components/FormInput.vue";
import FormDatepicker from "@/view/content/forms/components/FormDatepicker.vue";
import FormSelect from "@/view/content/forms/components/FormSelect.vue";
import FormSwitch from "@/view/content/forms/components/FormSwitch.vue";
import ServiceVisitCustomTaskRow from "@/modules/service-visits/components/ServiceVisitCustomTaskRow.vue";

export default {
  mixins: [validationMixin, formBuilderMixin],
  components: {
    FormInput,
    FormDatepicker,
    FormSelect,
    FormSwitch,
    ServiceVisitCustomTaskRow
  },
  props: {
    form: Object,
    visitId: {
      type: [Number, String],
      default: null
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      machines: [],
      statuses: [],
      itemStatuses: [],
      priorities: [],
      serviceLocations: [],
      mechanics: [],
      serviceTypes: [],
      machineServiceSignals: [],
      customTaskCounter: 0
    };
  },
  validations() {
    return {
      form: {
        machineId: { required },
        statusId: {
          required: requiredIf(() => this.showStatus),
          tasksTerminal: () =>
            !this.isFinishedStatusSelected || this.areVisitTasksTerminal
        },
        priorityId: { required },
        serviceLocationId: {
          required: requiredIf(() => !this.form.isOnRoad)
        },
        dateFrom: { required },
        dateTo: {
          required,
          afterDateFrom: value => this.isDateToAfterDateFrom(value)
        },
        duration: {
          required: requiredIf(() => !this.isOutsideService),
          minValue: value =>
            this.isOutsideService ||
            value === null ||
            value === "" ||
            Number(value) >= 0
        },
        serviceMileage: {
          required: requiredIf(() => this.isFinishedStatusSelected),
          integer: value =>
            !this.isFinishedStatusSelected ||
            (Number.isInteger(Number(value)) && Number(value) >= 0)
        },
        hoursWorked: {
          required: requiredIf(() => this.isFinishedStatusSelected),
          minValue: value =>
            !this.isFinishedStatusSelected ||
            (value !== null && value !== "" && Number(value) >= 0)
        }
      }
    };
  },
  computed: {
    showStatus() {
      return Object.prototype.hasOwnProperty.call(this.form, "statusId");
    },
    isOutsideService() {
      return this.selectedServiceLocation
        ? Boolean(this.selectedServiceLocation.isOutsideService)
        : false;
    },
    selectedServiceLocation() {
      return this.serviceLocations.find(
        item => Number(item.id) === Number(this.form.serviceLocationId)
      );
    },
    isFinishedStatusSelected() {
      const status = this.statuses.find(
        item => Number(item.id) === Number(this.form.statusId)
      );

      return Boolean(status && status.slug === "finished");
    },
    areVisitTasksTerminal() {
      const terminalStatusIds = this.itemStatuses
        .filter(status => ["finished", "cancelled"].includes(status.slug))
        .map(status => Number(status.id));
      const taskStatusIds = [
        ...(this.form.serviceTypeIds || []).map(
          id => (this.form.serviceTypeStatusIds || {})[id]
        ),
        ...(this.form.machineServiceSignalIds || []).map(
          id => (this.form.machineServiceSignalStatusIds || {})[id]
        ),
        ...(this.form.customTasks || []).map(task => task.statusId)
      ];

      return taskStatusIds.every(statusId => terminalStatusIds.includes(Number(statusId)));
    },
    completionMileageLabel() {
      const machine = this.machines.find(
        item => Number(item.id) === Number(this.form.machineId)
      );

      return machine && machine.isMileage
        ? this.$t("SERVICE_VISITS.COMPLETION_KILOMETERS")
        : this.$t("SERVICE_VISITS.COMPLETION_MOTO_HOURS");
    }
  },
  watch: {
    "form.isOnRoad"(value) {
      if (value) {
        this.form.serviceLocationId = null;
      } else {
        this.form.onRoadNote = null;
      }
    },
    isOutsideService(value) {
      if (value) {
        this.form.mechanicIds = [];
        this.form.duration = null;
      }
    }
  },
  mounted() {
    this.ensureTaskNoteState();
    this.loadMachines();
    this.loadStaticOptions();
    this.loadMachineOptions(false);
  },
  methods: {
    ensureTaskNoteState() {
      if (!this.form.serviceTypeNotes) {
        this.$set(this.form, "serviceTypeNotes", {});
      }
      if (!this.form.serviceTypeStatusIds) {
        this.$set(this.form, "serviceTypeStatusIds", {});
      }
      if (!this.form.machineServiceSignalNotes) {
        this.$set(this.form, "machineServiceSignalNotes", {});
      }
      if (!this.form.machineServiceSignalStatusIds) {
        this.$set(this.form, "machineServiceSignalStatusIds", {});
      }
    },
    validateState(name) {
      const field = this.$v.form[name];
      if (!field) {
        return null;
      }
      const { $dirty, $error } = field;
      return $dirty ? !$error : null;
    },
    loadMachines() {
      const params = this.$url.transformParams({
        active: 1,
        length: 1000,
        start: 0
      });
      return this.$store.dispatch(FETCH_MACHINES, params).then(data => {
        this.machines = (data.data.items || []).map(this.machineOption);
        this.ensureSelectedMachineOption();
      });
    },
    ensureSelectedMachineOption() {
      if (
        !this.form.machineId ||
        this.machines.some(machine => Number(machine.id) === Number(this.form.machineId))
      ) {
        return;
      }

      this.$store
        .dispatch(FETCH_MACHINE, this.form.machineId)
        .then(data => {
          if (!data.data) {
            return;
          }

          this.machines = [...this.machines, this.machineOption(data.data)];
        })
        .catch(() => {});
    },
    machineOption(machine) {
      return {
        ...machine,
        name: machine.serviceDisplayName || machine.name
      };
    },
    loadStaticOptions() {
      const listParams = this.$url.transformParams({
        length: 1000,
        start: 0
      });

      Promise.all([
        this.$store.dispatch(FETCH_SERVICE_VISIT_STATUSES),
        this.$store.dispatch(FETCH_SERVICE_VISIT_ITEM_STATUSES),
        this.$store.dispatch(FETCH_SERVICE_VISIT_PRIORITIES),
        this.$store.dispatch(FETCH_SERVICE_VISIT_SERVICE_LOCATIONS),
        this.$store.dispatch(FETCH_SERVICE_VISIT_MECHANICS, listParams)
      ]).then(([options, itemStatuses, priorities, serviceLocations, mechanics]) => {
        this.statuses = options.data || [];
        this.itemStatuses = itemStatuses.data || [];
        this.ensureCustomTaskStatuses();
        this.priorities = priorities.data || [];
        this.ensureVisitDefaults();
        this.serviceLocations = (serviceLocations.data || []).map(
          this.serviceLocationOption
        );
        this.mechanics = this.extractItems(mechanics).map(user => ({
          ...user,
          name: `${user.firstName || ""} ${user.lastName || ""}`.trim()
        }));
      });
    },
    loadMachineOptions(clearSelected = true) {
      if (!this.form.machineId) {
        this.serviceTypes = [];
        this.machineServiceSignals = [];
        if (clearSelected) {
          this.form.serviceTypeIds = [];
          this.form.machineServiceSignalIds = [];
          this.form.serviceTypeNotes = {};
          this.form.serviceTypeStatusIds = {};
          this.form.machineServiceSignalNotes = {};
          this.form.machineServiceSignalStatusIds = {};
        }
        return;
      }

      const params = {};
      params.machineId = this.form.machineId;
      if (this.visitId) {
        params.visitId = this.visitId;
      }

      Promise.all([
        this.$store.dispatch(
          FETCH_SERVICE_VISIT_SERVICE_TYPES,
          this.$url.transformParams({
            machineId: params.machineId,
            serviceGroupName: "Обслужване"
          })
        ),
        this.$store.dispatch(
          FETCH_SERVICE_VISIT_MACHINE_SERVICE_SIGNALS,
          this.$url.transformParams(params)
        )
      ]).then(([serviceTypes, machineServiceSignals]) => {
        this.serviceTypes = serviceTypes.data || [];
        this.machineServiceSignals = machineServiceSignals.data || [];
        if (clearSelected) {
          this.form.serviceTypeIds = [];
          this.form.machineServiceSignalIds = [];
          this.form.serviceTypeNotes = {};
          this.form.serviceTypeStatusIds = {};
          this.form.machineServiceSignalNotes = {};
          this.form.machineServiceSignalStatusIds = {};
        }
      });
    },
    extractItems(response) {
      if (!response || !response.data) {
        return [];
      }
      if (Array.isArray(response.data)) {
        return response.data;
      }
      return response.data.items || [];
    },
    serviceLocationOption(location) {
      const nameParts = [location.name, location.location].filter(Boolean);

      return {
        id: location.id,
        name: nameParts.join(" - "),
        location: location.location,
        serviceLocationTypeSlug: location.serviceLocationTypeSlug,
        isOutsideService: Boolean(location.isOutsideService)
      };
    },
    onMachineChange() {
      if (this.readonly) {
        return;
      }

      this.loadMachineOptions(true);
    },
    isDateToAfterDateFrom(value) {
      if (!value || !this.form.dateFrom) {
        return true;
      }

      return new Date(value).getTime() >= new Date(this.form.dateFrom).getTime();
    },
    addCustomTask() {
      if (this.readonly) {
        return;
      }

      this.customTaskCounter += 1;
      this.form.customTasks.push({
        localId: `new-${this.customTaskCounter}`,
        description: "",
        statusId: this.defaultItemStatusId(),
        note: ""
      });
    },
    removeCustomTask(index) {
      if (this.readonly) {
        return;
      }

      this.form.customTasks.splice(index, 1);
    },
    ensureCustomTaskStatuses() {
      const defaultStatusId = this.defaultItemStatusId();

      if (!defaultStatusId) {
        return;
      }

      (this.form.customTasks || []).forEach(customTask => {
        if (!customTask.statusId) {
          this.$set(customTask, "statusId", defaultStatusId);
        }
      });
    },
    ensureVisitDefaults() {
      if (!this.form.statusId) {
        const newStatus = this.statuses.find(status => status.slug === "new");
        this.$set(this.form, "statusId", newStatus ? newStatus.id : null);
      }

      if (!this.form.priorityId) {
        const normalPriority = this.priorities.find(
          priority => priority.slug === "normal"
        );
        this.$set(
          this.form,
          "priorityId",
          normalPriority ? normalPriority.id : null
        );
      }
    },
    formatLastService(service) {
      const parts = [];
      if (service.date) {
        parts.push(this.formatDate(service.date));
      }

      if (service.mileage !== null && service.mileage !== undefined) {
        const unit = service.isMileage ? this.$t("LABELS.KM") : "ч.";
        parts.push(`${service.mileage} ${unit}`);
      }

      return parts.length ? parts.join(" | ") : this.$t("LABELS.NA");
    },
    formatDate(value) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }

      return [
        String(date.getDate()).padStart(2, "0"),
        String(date.getMonth() + 1).padStart(2, "0"),
        date.getFullYear()
      ].join(".");
    },
    isServiceTypeSelected(id) {
      return (this.form.serviceTypeIds || []).map(Number).includes(Number(id));
    },
    serviceTypeNote(id) {
      return (this.form.serviceTypeNotes || {})[id] || "";
    },
    setServiceTypeNote(id, value) {
      if (this.readonly) {
        return;
      }

      this.ensureTaskNoteState();
      this.$set(this.form.serviceTypeNotes, id, value);
    },
    serviceTypeStatusId(id) {
      return (this.form.serviceTypeStatusIds || {})[id] || this.defaultItemStatusId();
    },
    setServiceTypeStatusId(id, value) {
      if (this.readonly) {
        return;
      }

      this.ensureTaskNoteState();
      this.$set(this.form.serviceTypeStatusIds, id, value);
    },
    isMachineServiceSignalSelected(id) {
      return (this.form.machineServiceSignalIds || [])
        .map(Number)
        .includes(Number(id));
    },
    machineServiceSignalNote(id) {
      return (this.form.machineServiceSignalNotes || {})[id] || "";
    },
    setMachineServiceSignalNote(id, value) {
      if (this.readonly) {
        return;
      }

      this.ensureTaskNoteState();
      this.$set(this.form.machineServiceSignalNotes, id, value);
    },
    machineServiceSignalStatusId(id) {
      return (this.form.machineServiceSignalStatusIds || {})[id] || this.defaultItemStatusId();
    },
    setMachineServiceSignalStatusId(id, value) {
      if (this.readonly) {
        return;
      }

      this.ensureTaskNoteState();
      this.$set(this.form.machineServiceSignalStatusIds, id, value);
    },
    defaultItemStatusId() {
      const newStatus = this.itemStatuses.find(status => status.slug === "new");

      return newStatus ? newStatus.id : (this.itemStatuses[0] || {}).id || null;
    }
  }
};
</script>
